import {FULFILLED, PENDING} from 'redux-promise-middleware';
import {uniqBy} from 'ramda';
import {SagaIterator} from 'redux-saga';
import {sendLoginStatus, pushDataLayer, sendEvent} from '@optimaxdev/analytics/mobile';

import {CART, QUOTE_ITEM, CUSTOMER} from 'constants/actions';
import {CATEGORY_STORE, HP_FEATURED_STORE} from 'constants/category';
import {CATEGORY_LIST} from 'constants/analytics/storages';
import {ActionType} from 'constants/flow/flowTypes';

import {getCartKey, getQuoteItems} from 'selectors/cart/cart';
import {takeEvery, select, call, take} from 'redux-saga/effects';
import {sendEmailToDataLayer} from 'libs/analytics/analytics';
import {setCustomerEmail} from 'reducers/analytics/dataLayer';
import {getDataLayer} from 'selectors/analytics/dataLayer';
import {sessionStore, localStore} from 'libs/storage/storage';
import {getCustomer} from 'selectors/customer/customer.js';
import {addToCartV2} from 'actions/cart/cart';
import {LOGIN_METHOD} from 'constants/login';
import {getAddToCartData} from 'selectors/dynamicYield/dynamicYield';

export const LOGIN_STATUS = 'login_status';

/**
 * Save cartid in dataLayer.
 */
export function* pushCartIdToDataLayer() {
    const shareId = yield select(getCartKey);
    if (shareId)
        yield call(pushDataLayer, {
            CartID: shareId,
        });
}

/**
 * Send Customer email to dataLayer.
 */
export function* sendCustomerEmailToDataLayer() {
    const {email} = yield select(getDataLayer);
    yield call(sendEmailToDataLayer, email);
}

/**
 * Prepare a list of categories for products added to the cart.
 *
 * @param {ActionType<any, {productId: number}>} action - Add to cart action.
 */
export function* prepareCategoryList(action: ActionType<any, {productId: number}>) {
    if (!action.meta) return;
    const {productId} = action.meta;
    const category = yield call([sessionStore, 'get'], CATEGORY_STORE);
    const hpCategory = yield call([sessionStore, 'get'], HP_FEATURED_STORE);
    const categoryList = yield call([localStore, 'get'], CATEGORY_LIST);

    const {name: categoryName = ''} = category || hpCategory || {};
    const newCategory = {
        id: productId,
        category: categoryName,
    };

    yield call(
        [localStore, 'set'],
        CATEGORY_LIST,
        categoryList ? uniqBy(item => item.id, [newCategory, ...categoryList]) : [newCategory],
    );
}

/**
 * Update category list.
 */
export function* updateCategoryList() {
    const quoteItems = yield select(getQuoteItems);
    const productIdList = quoteItems.map(item => Number(item.product_id));
    const categoryList = yield call([localStore, 'get'], CATEGORY_LIST);
    if (categoryList)
        yield call(
            [localStore, 'set'],
            CATEGORY_LIST,
            categoryList.filter(category => productIdList.includes(category.id)),
        );
}

/**
 * Send event about user status
 */
export function* sendLoginStatusEvent() {
    const {login_in_status: loginStatus, is_logged_in: isUserLogged} = yield select(getCustomer);
    const previousLoginStatus = yield call([sessionStore, 'get'], LOGIN_STATUS);
    const currentStatus = loginStatus === 'subscribed' && isUserLogged ? 'registered' : loginStatus;
    if (currentStatus === previousLoginStatus) return;
    const loginMethod = localStore.get(LOGIN_METHOD);

    switch (currentStatus) {
        case previousLoginStatus:
            return;
        case 'guest':
            yield call(sendLoginStatus, currentStatus);
            break;
        default:
            yield call(sendLoginStatus, currentStatus, loginMethod || '');
    }
    yield call([sessionStore, 'set'], LOGIN_STATUS, currentStatus);
}

/**
 * Send event that a user has added a product to the cart
 */
export function* addToCartDY(): SagaIterator {
    yield take(`${QUOTE_ITEM.GET_ITEMS}_FULFILLED`);

    const eventDataSelector = yield select(getAddToCartData);
    const eventData = {
        name: 'Add to Cart',
        properties: {
            dyType: 'add-to-cart-v1',
            ...eventDataSelector,
        },
    };

    yield call(sendEvent, 'event', eventData);
}

/**
 * Begin of saga.
 */
export default function* analyticsSaga() {
    yield takeEvery(`${CUSTOMER.GET_CUSTOMER_DATA}_${FULFILLED}`, sendLoginStatusEvent);
    yield takeEvery(setCustomerEmail, sendCustomerEmailToDataLayer);
    yield takeEvery(`${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`, pushCartIdToDataLayer);
    yield takeEvery(
        [`${CART.ADD_TO_CART}_${FULFILLED}`, `${addToCartV2}_${PENDING}`],
        prepareCategoryList,
    );
    yield takeEvery(
        [`${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`, `${CART.REMOVE_QUOTE_ITEM}_${FULFILLED}`],
        updateCategoryList,
    );
    yield takeEvery(`${addToCartV2}_${FULFILLED}`, addToCartDY);
}
